import React, { useEffect, useMemo, useRef } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import ReactRouterPropTypes from 'react-router-prop-types';
import { useParams } from 'react-router-dom';
import { scrollPageTo } from 'arts/config/lib';
import * as AppClasses from '../Application/styles.module.scss';
import Filter from '../works/filter';
import GTMPageView from '../Application/data_layer';
import CategoryDescription from '../category/category_description';
import BreadCrumbsGeneric from '../bread_crumbs';
import Paginator from '../works/paginator';
import GridSelection, { THUMB } from '../walls/grid_selection';
import { BREADCRUMBS_LINKS } from '../walls/shared';
import * as WorkClasses from '../works/styles.module.scss';
import * as Classes from '../walls/styles.module.scss';
import Wall from '../walls_as_grid/wall';
import { trackEvent } from '../../config/analytics';
import { humanizeParams } from '../works/works';
import WallSkeleton from '../wall/skeleton';

const WITH_HOME_DECOR_STYLE_ID = 'with_home_decor_style_id';
const WITH_ROOM_ID = 'with_room_id';

export const VALID_QUERY_PARAMS = [
  'newest',
  WITH_HOME_DECOR_STYLE_ID,
  WITH_ROOM_ID,
  'with_price',
  'query',
  'with_work_id',
  'page'
];

const GALERRY_WALLS = 'Gallery Walls';

const getParams = (params, pathname, search, wallFilters) => {
  const parsedParams = queryString.parse(search);
  const out = {};

  let title = GALERRY_WALLS;

  VALID_QUERY_PARAMS.forEach(key => {
    const val = parsedParams[key];
    if (val) {
      out[key] = val;
    }
  });

  const cleanParams = { ...out };
  const skipFilterKeys = [];

  const {
    with_home_decor_style_id: homeDecorStyleId,
    with_room_id: roomId
  } = (params || {});

  if (homeDecorStyleId) {
    out[WITH_HOME_DECOR_STYLE_ID] = homeDecorStyleId;
    skipFilterKeys.push(WITH_HOME_DECOR_STYLE_ID);
  }

  if (roomId) {
    out[WITH_ROOM_ID] = roomId;
    skipFilterKeys.push(WITH_ROOM_ID);
  }

  if (pathname.match(/\/walls\/room/) && !out[WITH_ROOM_ID]) {
    const slug = pathname.split('/').pop();
    const id = parseInt(window.slugsMaps.rooms[slug], 10);
    const filter = (wallFilters || []).find(f => f.key === 'with_room_id');
    out[WITH_ROOM_ID] = id;
    skipFilterKeys.push(WITH_ROOM_ID);
    title = filter ? filter.options.find(o => o.id === id).name : '';
    title = `${ title } ${ GALERRY_WALLS }`;
  }

  if (pathname.match(/\/walls\/style/) && !out[WITH_HOME_DECOR_STYLE_ID]) {
    const slug = pathname.split('/').pop();
    const id = parseInt(window.slugsMaps.home_decor_styles[slug], 10);
    const filter = (wallFilters || []).find(f => f.key === 'with_home_decor_style_id');
    out[WITH_HOME_DECOR_STYLE_ID] = id;
    skipFilterKeys.push(WITH_HOME_DECOR_STYLE_ID);
    title = filter ? filter.options.find(o => o.id === id).name : '';
    title = `${ title } ${ GALERRY_WALLS }`;
  }

  if (!out.page) {
    out.page = 1;
  }
  out.page = parseInt(out.page, 10);

  return [out, cleanParams, skipFilterKeys, title];
};

const Walls20 = ({
  walls,
  totalPages,
  loading,
  wallFilters,
  location,
  history,
  fetchWallFilters,
  fetchWalls
}) => {
  const params = useParams();

  const [
    queryParams, cleanParams, skipFilterKeys, title
  ] = useMemo(
    () => getParams(params, location.pathname, location.search, wallFilters),
    [location.pathname, location.search, wallFilters]
  );

  useEffect(() => {
    if (!wallFilters) {
      fetchWallFilters();
    }
  }, []);

  useEffect(() => {
    scrollPageTo(0, 250);
    fetchWalls(queryParams);
  }, [location.search, location.pathname]);

  useEffect(() => {
    if (!loading && walls.length && wallFilters) {
      const humanParams = humanizeParams(queryParams, wallFilters);
      trackEvent('walls center', { ...humanParams, as: 'grid', 'walls count': walls.length });
    }
  }, [loading, walls.length, wallFilters]);

  const parentRef = useRef(null);

  const loadingOrNoWalls = () => {
    if (loading) {
      return (
        <>
          <WallSkeleton />
          <WallSkeleton />
          <WallSkeleton />
          <WallSkeleton />
          <WallSkeleton />
          <WallSkeleton />
        </>
      );
    }
    if (walls.length === 0) {
      return <div>There are no results that match your selection - please try again.</div>;
    }
    return null;
  };

  return (
    <>
      <div className={ AppClasses.container20 }>
        <div className={ AppClasses.innerContainer20 }>
          <div className={ Classes.title }>
            <h1>{ title }</h1>
          </div>
          <GTMPageView pageType="category" section="gallery walls" />
          <Filter
            path={ location.pathname }
            label="Search walls by"
            history={ history }
            { ...queryParams }
            filters={ VALID_QUERY_PARAMS.filter(p => p !== 'page') }
            artFilters={ wallFilters }
            skipFilterKeys={ skipFilterKeys }
            defaultPath="/walls"
          >
            <div className={ Classes.bcrumbs }>
              <BreadCrumbsGeneric className={ Classes.wallBc } links={ BREADCRUMBS_LINKS }>
                <GridSelection path="/walls" grid={ THUMB } params={ queryParams } />
              </BreadCrumbsGeneric>
            </div>
          </Filter>
        </div>
      </div>
      <div className={ AppClasses.container20 }>
        <div className={ AppClasses.innerContainer20 }>
          <Paginator
            path={ location.pathname }
            params={ cleanParams }
            page={ queryParams.page }
            totalPages={ totalPages || 1 }
            history={ history }
            className={ WorkClasses.small }
          />
          <ul className={ classnames(AppClasses.gridContainer2inRow, Classes.grid) }>
            {
              walls.length > 0 ? (
                walls.map((wall, position) => (
                  <Wall
                    key={ wall.id }
                    { ...wall }
                    parentRef={ parentRef }
                    href={ `${ wall.href }?as=thumb&${ queryString.stringify({ ...queryParams }) }` }
                    position={ position }
                  />
                ))
              ) : loadingOrNoWalls()
            }
          </ul>
          <Paginator
            path={ location.pathname }
            params={ cleanParams }
            page={ queryParams.page }
            totalPages={ totalPages || 1 }
            history={ history }
            className={ WorkClasses.small }
          />
          <CategoryDescription loadadble />
        </div>
      </div>
    </>
  );
};

Walls20.propTypes = {
  walls: PropTypes.arrayOf(PropTypes.any),
  totalPages: PropTypes.number,
  loading: PropTypes.bool,
  wallFilters: PropTypes.arrayOf(PropTypes.any),
  location: ReactRouterPropTypes.location.isRequired,
  history: ReactRouterPropTypes.history.isRequired,
  fetchWallFilters: PropTypes.func.isRequired,
  fetchWalls: PropTypes.func.isRequired
};

export default Walls20;
